import type { ThemeId } from "./types";

export type ThemePalette = {
  background: string;
  surface: string;
  ink: string;
  accent: string;
};

export type ThemeDefinition = {
  id: ThemeId;
  label: string;
  description: string;
  features: string[];
  palette: ThemePalette;
};

export const PROFILE_THEMES: ThemeDefinition[] = [
  {
    id: "paper",
    label: "Journal",
    description: "带校样批注与纸张质感的独立文化杂志",
    features: ["编辑", "纸张", "长文"],
    palette: {
      background: "#f1ece2",
      surface: "#fffdf7",
      ink: "#20231f",
      accent: "#a94f38"
    }
  },
  {
    id: "poster",
    label: "Signal",
    description: "瑞士广播图形，让身份与行动像信号一样抵达",
    features: ["广播", "网格", "高对比"],
    palette: {
      background: "#f4f1e8",
      surface: "#223bff",
      ink: "#12151a",
      accent: "#ffd84a"
    }
  },
  {
    id: "index",
    label: "Mosaic",
    description: "新包豪斯模块场，让短介绍也有鲜明构图",
    features: ["包豪斯", "模块", "结构化"],
    palette: {
      background: "#eef0f6",
      surface: "#ffffff",
      ink: "#171922",
      accent: "#6d5bd0"
    }
  },
  {
    id: "riso",
    label: "Riso",
    description: "带套色错位与半调网点的独立印刷拼贴",
    features: ["印刷", "拼贴", "高能"],
    palette: {
      background: "#f3e7d3",
      surface: "#fff8e8",
      ink: "#171514",
      accent: "#e64b35"
    }
  },
  {
    id: "prism",
    label: "Prism",
    description: "用折射光谱与透明层次呈现轻盈数字身份",
    features: ["光谱", "叠层", "未来"],
    palette: {
      background: "#f2f1fa",
      surface: "#ffffff",
      ink: "#1e2030",
      accent: "#7657ff"
    }
  },
  {
    id: "field",
    label: "Field",
    description: "像自然观察记录一样安静、温暖、有秩序",
    features: ["自然", "观察", "留白"],
    palette: {
      background: "#e8e4d7",
      surface: "#f7f3e8",
      ink: "#263027",
      accent: "#a9563f"
    }
  },
  {
    id: "noir",
    label: "Noir",
    description: "电影片头式黑色画廊，克制、私密、有余韵",
    features: ["电影", "胶片", "画廊"],
    palette: {
      background: "#050607",
      surface: "#111614",
      ink: "#f7f4eb",
      accent: "#6ec6a2"
    }
  },
  {
    id: "terminal",
    label: "Terminal",
    description: "现代硬件控制台，精确、克制、带一点荧光",
    features: ["控制台", "状态", "命令"],
    palette: {
      background: "#050806",
      surface: "#07100b",
      ink: "#e9f4ec",
      accent: "#73e69a"
    }
  },
  {
    id: "chrome",
    label: "Raw",
    description: "把 Markdown 控制符与原始内容完整摊开的一张白纸",
    features: ["原文", "Markdown", "白纸"],
    palette: {
      background: "#ececec",
      surface: "#ffffff",
      ink: "#171717",
      accent: "#666666"
    }
  },
  {
    id: "pixel",
    label: "Pixel",
    description: "可横向切屏的掌机像素身份系统",
    features: ["像素", "切屏", "掌机"],
    palette: {
      background: "#080a12",
      surface: "#151a28",
      ink: "#b7ff45",
      accent: "#ff4fa3"
    }
  },
  {
    id: "arcade",
    label: "Arcade",
    description: "带实时 HUD 与纵向关卡的街机式介绍",
    features: ["关卡", "HUD", "街机"],
    palette: {
      background: "#140a32",
      surface: "#fff3d1",
      ink: "#140a32",
      accent: "#ff4b3e"
    }
  },
  {
    id: "noise",
    label: "Noise",
    description: "可重排的数字噪声与滚动错位构图",
    features: ["噪声", "错位", "实验"],
    palette: {
      background: "#f1eee6",
      surface: "#121212",
      ink: "#121212",
      accent: "#ff2d8d"
    }
  },
  {
    id: "folio",
    label: "Folio",
    description: "安静的现代作品集，用年份栏和留白组织履历与作品",
    features: ["极简", "作品集", "年份栏"],
    palette: {
      background: "#f7f6f2",
      surface: "#ffffff",
      ink: "#1c1c1a",
      accent: "#3451d1"
    }
  },
  {
    id: "ink",
    label: "Ink",
    description: "中文优先的书卷排版，宋体大字、朱印与宽松行距",
    features: ["中文", "书卷", "朱印"],
    palette: {
      background: "#f3eee3",
      surface: "#fbf8f1",
      ink: "#1f1d1a",
      accent: "#b23a2b"
    }
  },
  {
    id: "bento",
    label: "Bento",
    description: "便当格信息卡，让当前方向、作品和资源各占一格",
    features: ["网格", "卡片", "结构化"],
    palette: {
      background: "#ecebe6",
      surface: "#ffffff",
      ink: "#121212",
      accent: "#ff5a1f"
    }
  },
  {
    id: "letter",
    label: "Letter",
    description: "一封第一人称的自我介绍信，以签名收尾、附言留联系",
    features: ["信笺", "温度", "第一人称"],
    palette: {
      background: "#e7e1d6",
      surface: "#fffbf3",
      ink: "#2a2622",
      accent: "#2c4370"
    }
  },
  {
    id: "exhibit",
    label: "Exhibit",
    description: "美术馆展签式作品陈列，每件作品都有自己的展牌",
    features: ["展览", "作品", "展签"],
    palette: {
      background: "#f4f3ef",
      surface: "#ffffff",
      ink: "#141414",
      accent: "#d9482b"
    }
  },
  {
    id: "blueprint",
    label: "Blueprint",
    description: "工程蓝图与规格书，用零件表和修订记录呈现履历",
    features: ["蓝图", "规格", "工程"],
    palette: {
      background: "#123f7a",
      surface: "#174a8c",
      ink: "#eaf2ff",
      accent: "#ffd166"
    }
  }
];

const THEME_IDS = new Set(PROFILE_THEMES.map((theme) => theme.id));
const LEGACY_THEME_MAP: Record<string, ThemeId> = {
  plain: "paper",
  creator: "poster",
  studio: "poster",
  dev: "terminal"
};

export function normalizeThemeId(themeId: unknown): ThemeId {
  if (typeof themeId !== "string") return "paper";
  if (THEME_IDS.has(themeId as ThemeId)) return themeId as ThemeId;
  return LEGACY_THEME_MAP[themeId] ?? "paper";
}

export function getThemeDefinition(themeId: unknown): ThemeDefinition {
  const normalized = normalizeThemeId(themeId);
  return PROFILE_THEMES.find((theme) => theme.id === normalized) ?? PROFILE_THEMES[0];
}
